import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, throwError, map } from 'rxjs';
import { Router } from '@angular/router';
import { 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest, 
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthState,
  User,
  UserRole 
} from '../types';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  
  private readonly API_URL = '/api/auth';
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_data';

  private authState$ = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
    loading: false,
    error: null
  });

  constructor() {
    this.initializeAuth();
  }

  // Public observables
  get isAuthenticated$(): Observable<boolean> {
    return this.authState$.pipe(
      map(state => state.isAuthenticated)
    );
  }

  get user$(): Observable<User | null> {
    return this.authState$.pipe(
      map(state => state.user)
    );
  }

  get authState(): AuthState {
    return this.authState$.value;
  }

  // Authentication methods
  login(credentials: LoginRequest): Observable<AuthResponse> {
    this.setLoading(true);
    
    return this.http.post<AuthResponse>(`${this.API_URL}/login`, credentials)
      .pipe(
        tap(response => {
          if (response.success) {
            this.setAuthData(response.data);
            this.router.navigate(['/dashboard']);
          }
        }),
        catchError(error => {
          this.setError(error.error?.message || 'Login failed');
          return throwError(() => error);
        }),
        tap(() => this.setLoading(false))
      );
  }

  register(userData: RegisterRequest): Observable<AuthResponse> {
    this.setLoading(true);
    
    return this.http.post<AuthResponse>(`${this.API_URL}/register`, userData)
      .pipe(
        tap(response => {
          if (response.success) {
            this.setAuthData(response.data);
            this.router.navigate(['/dashboard']);
          }
        }),
        catchError(error => {
          this.setError(error.error?.message || 'Registration failed');
          return throwError(() => error);
        }),
        tap(() => this.setLoading(false))
      );
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/forgot-password`, request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/reset-password`, request);
  }

  // Simple login for demo purposes
  simpleLogin(email: string, password: string): void {
    const mockUser: User = {
      id: '1',
      email: email,
      firstName: 'Padmapper',
      lastName: 'Forty5',
      username : 'PadmapperForty5',
      role: UserRole.USER,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    this.setAuthData({
      user: mockUser,
      accessToken: 'mock-token',
      refreshToken: 'mock-refresh-token'
    });
    
    this.router.navigate(['/profile']);
  }

  logout(): void {
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<any>(`${this.API_URL}/refresh`, { refreshToken })
      .pipe(
        tap(response => {
          if (response.success) {
            this.updateTokens(response.data.accessToken, response.data.refreshToken);
          }
        }),
        catchError(error => {
          this.logout();
          return throwError(() => error);
        })
      );
  }

  // Helper methods
  private initializeAuth(): void {
    const token = this.getAccessToken();
    const user = this.getStoredUser();
    
    if (token && user) {
      this.authState$.next({
        ...this.authState$.value,
        isAuthenticated: true,
        user,
        accessToken: token,
        refreshToken: this.getRefreshToken()
      });
    }
  }

  private setAuthData(data: { user: User; accessToken: string; refreshToken: string }): void {
    localStorage.setItem(this.TOKEN_KEY, data.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(data.user));
    localStorage.setItem('isLogin', 'true');

    this.authState$.next({
      ...this.authState$.value,
      isAuthenticated: true,
      user: data.user,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      error: null
    });
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('isLogin');
    
    this.authState$.next({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      error: null
    });
  }

  private updateTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    
    this.authState$.next({
      ...this.authState$.value,
      accessToken,
      refreshToken
    });
  }

  private setLoading(loading: boolean): void {
    this.authState$.next({
      ...this.authState$.value,
      loading
    });
  }

  private setError(error: string): void {
    this.authState$.next({
      ...this.authState$.value,
      error
    });
  }

  // Token management
  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private getStoredUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  isTokenExpired(token?: string): boolean {
    if (!token) token = this.getAccessToken() ?? undefined;
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }
}
